/** Encode path segments defensively */
const enc = (v: string | number) => encodeURIComponent(String(v))

/** Full set of connected-account path helpers (vessel-scoped). Used by createVesselConnectedAccountPaths. */
type VesselConnectedAccountPaths = {
  // Beneficiaries
  beneficiaries: () => string
  beneficiaryById: (beneficiaryId: string) => string
  beneficiary: () => string

  // Cards
  cards: () => string
  cardById: (cardId: string) => string
  cardLimits: (cardId: string) => string
  cardSpendingStats: (cardId: string) => string
  cardSearch: (cardId: string) => string
  cardDeliveryDetails: (cardId: string) => string
  cardStatus: (cardId: string) => string
  cardActivate: (cardId: string) => string
  myCardStatus: (cardId: string) => string
  cardPanToken: (cardId: string) => string
  myCards: () => string

  // Cardholders
  cardholders: () => string
  cardholdersAvailable: () => string
  cardholder: () => string
  cardholderById: (cardholderId: string) => string
  cardholderCard: (cardholderId: string) => string
  cardholderDetails: (cardholderId: string) => string

  // Transactions
  categories: () => string
  transactions: () => string
  myTransactions: () => string
  transactionById: (transactionId: string) => string
  transactionEditInspect: (transactionId: string) => string
  transactionCommit: (transactionId: string) => string
  transactionStatus: (transactionId: string) => string
  transactionStatusInspect: (transactionId: string) => string
  transactionPaymentInitiation: (transactionId: string) => string
  transactionSettlementStatus: (transactionId: string) => string
  transactionTransfer: (transactionId: string) => string
  transactionTransferFees: (transactionId: string) => string
  transactionAttachments: (transactionId: string) => string
  transactionAttachmentById: (transactionId: string, attachmentId: string) => string
  transactionAttachmentPrepare: (transactionId: string) => string
  transactionAttachmentConfirm: (transactionId: string) => string
  transactionActivityLogs: (transactionId: string) => string
  opsTransaction: () => string
  opsTransactionById: (transactionId: string) => string
  receiptCapture: () => string
  taxLabels: () => string

  // Charters
  chartersSearch: () => string
  chartersForTransactions: () => string
  charter: () => string
  charterById: (charterId: string) => string
  charterStats: (charterId: string) => string
  charterTransactions: (charterId: string) => string
  charterLedger: (charterId: string) => string
  charterSigningOffLedger: (charterId: string) => string
  charterDigitalSignOffLedger: (charterId: string) => string
  charterDigitalSigningOffLedger: (charterId: string) => string
  charterTransaction: (charterId: string) => string
  charterAttachments: (charterId: string) => string
  charterAttachmentById: (charterId: string, attachmentId: string) => string
  charterAttachmentPrepare: (charterId: string) => string
  charterAttachmentConfirm: (charterId: string) => string

  // Deposits
  deposits: () => string
  depositById: (depositId: string) => string
  depositAttribution: () => string

  cashFloats: () => string
  cashFloatById: (cashFloatId: string) => string
  cashFloatBalance: (cashFloatId: string) => string
  cashFloatActivity: (cashFloatId: string) => string
  cashFloatTopUp: (cashFloatId: string) => string
  /** Aggregated cash float totals per display currency (GET `/cash/summary`). */
  cashSummary: () => string

  // Users
  connectedAccountUsersInvites: () => string
  connectedAccountAllUsers: () => string
  addUsersToConnectedAccount: () => string
  removeUsersFromConnectedAccount: () => string
  onboardingAuthorize: () => string
  rfiAuthorize: () => string

  // Wallet / Global Accounts
  globalAccounts: () => string
  globalAccount: () => string
  globalAccountById: (globalAccountId: string) => string
  // @deprecated - used as a fallback if the feature flag "PAYOUT_TRANSFER" is disabled. The enabled flow should use transfers()
  payout: () => string
  // POST `/transfers` — vessel-scoped.
  transfers: () => string
  transferById: (transferId: string) => string
  transfersValidate: () => string
  payoutAuthorize: () => string
  fxQuotes: () => string
  fxQuoteById: (quoteId: string) => string
  fxConversions: () => string
  fxConversionById: (conversionId: string) => string
  balancesHistory: () => string
  balancesFinancialTransactions: () => string
  balancesFinancialTransactionById: (transactionId: string) => string
  balancesFinancialTransactionBySource: (source: string) => string
  balancesTotalEstimate: (currency: string) => string
  balancesCurrent: () => string
  scaAuthorize: () => string
  connectedAccountTransfer: () => string
  // Reports
  financialReports: () => string
  financialReportById: (reportId: string) => string
  generatedReports: () => string
  generatedReportById: (reportId: string) => string
  generatedReportsExpenseReport: () => string
  generatedReportsXeroBankStatement: () => string

  // Owes
  apaOwesOpsTransactions: () => string
  transactionConnectedAccountTransfer: (transactionId: string) => string

  // Xero
  xeroConnectionSync: () => string

  // QuickBooks
  quickbooksConnectionSync: () => string

  // Approval workflows
  approvalWorkflows: () => string
  approvalWorkflowById: (workflowId: string) => string
  approvalWorkflowSettings: () => string
  approvalInitiatorCandidates: () => string
}

type IamOrganizationPaths = {
  // Organisation users
  organizationUsers: () => string
  organizationUsersWithAccess: () => string
  organizationUser: (userId: string) => string
  organizationUserRoles: (userId: string) => string
  leaveOrganization: () => string

  // Vessel users
  vesselUsers: (vesselId: string) => string
}

type OrganizationPaths = {
  // Invites
  organizationInvites: () => string
  createOrganizationInvite: () => string
  getOrganizationInvite: (inviteId: string) => string
  deleteOrganizationInvite: (inviteId: string) => string

  // User Access
  userAccessToConnectedAccounts: () => string
  addUserToConnectedAccounts: () => string
  removeUserFromConnectedAccounts: () => string

  // Categories
  categoriesAll: () => string
  category: () => string
  categoryById: (categoryId: string) => string

  // Vessels
  vessels: () => string
  vesselConnectedAccounts: (vesselId: string) => string
  vesselConnectedAccountsAvailability: (vesselId: string) => string

  // Integrations
  integrations: () => string
  integrationsToggle: () => string

  // Xero
  xeroOrganizations: () => string
  xeroOrgMappings: () => string
  xeroEntityVessels: () => string
  xeroChartOfAccounts: () => string
  xeroTaxRateImports: () => string
  xeroTaxRates: (xeroTenantId: string) => string
  xeroTaxRateMappings: () => string
  xeroBankAccounts: (xeroTenantId: string) => string
  xeroCurrencyBankMappings: () => string
  xeroTrackingCategories: () => string
}

type IntegrationPaths = {
  xeroOAuthConnect: () => string
  quickbooksOAuthConnect: () => string
}

type OrganizationConnectedAccountPaths = {
  // Xero
  xeroTrackingOptions: () => string
  quickbooksConfiguration: () => string
  quickbooksConnection: () => string
  quickbooksAccounts: () => string
  quickbooksBankAccounts: () => string
  quickbooksCurrencyBankMappings: () => string
  quickbooksTaxCodes: () => string
  quickbooksTaxMappings: () => string
}

type VesselPaths = {
  invite: () => string
  vesselUsersInvites: () => string
  connectedAccounts: () => string
  connectedAccountsAvailability: () => string
  activateModule: () => string
  /** GET cash floats usable for a module (vessel-only; `/cash-float/available`). */
  cashFloatsAvailable: () => string
  /** POST transfer between cash floats (vessel-scoped; not under connected-account prefix). */
  cashTransfer: () => string
}

type RootPaths = {
  readonly me: () => string
  readonly mePreferences: () => string
  readonly config: () => string
  readonly getInvite: (inviteId: string) => string
}

/**
 * Size-limited cache implementation that evicts the oldest entry when limit is reached.
 * Uses FIFO eviction: when the cache is full, the first (oldest) entry is removed.
 * When updating an existing key, it's moved to the end (newest position).
 *
 * How it works:
 * - JavaScript Maps maintain insertion order
 * - `map.keys().next().value` gets the first (oldest) key
 * - Deleting and re-adding a key moves it to the end (newest)
 */
export class SizeLimitedCache<V> {
  private cache = new Map<string, V>()
  private readonly maxSize: number

  constructor(maxSize: number) {
    this.maxSize = maxSize
  }

  get(key: string): V | undefined {
    return this.cache.get(key)
  }

  set(key: string, value: V): void {
    // If key already exists, update it (this maintains insertion order)
    if (this.cache.has(key)) {
      this.cache.delete(key)
    } else if (this.cache.size >= this.maxSize) {
      // Evict the oldest entry (first in Map iteration order)
      const firstKey = this.cache.keys().next().value
      if (firstKey !== undefined) {
        this.cache.delete(firstKey)
      }
    }
    this.cache.set(key, value)
  }
}

// Maximum number of cached path objects per cache type
// Reasonable limit for a typical SPA session (most users won't switch between more than 10-20 orgs/CAs)
const MAX_CACHE_SIZE = 50

// Cache for IAM organization paths objects (keyed by orgId)
const iamOrganizationPathsCache = new SizeLimitedCache<IamOrganizationPaths>(MAX_CACHE_SIZE)

// Cache for organization paths objects (keyed by orgId)
const organizationPathsCache = new SizeLimitedCache<OrganizationPaths>(MAX_CACHE_SIZE)
const organizationConnectedAccountPathsCache = new SizeLimitedCache<OrganizationConnectedAccountPaths>(MAX_CACHE_SIZE)

// Cache for integration paths objects (keyed by orgId)
const integrationPathsCache = new SizeLimitedCache<IntegrationPaths>(MAX_CACHE_SIZE)

// Cache for vessel paths objects (keyed by JSON stringified [orgId, vesselId])
const vesselPathsCache = new SizeLimitedCache<VesselPaths>(MAX_CACHE_SIZE)

// Cache for vessel connected account paths (keyed by JSON stringified [orgId, vesselId, connectedAccountId])
const vesselConnectedAccountPathsCache = new SizeLimitedCache<VesselConnectedAccountPaths>(MAX_CACHE_SIZE)

// Singleton root paths object (no parameters, so we can cache a single instance)
let rootPathsCache: RootPaths | null = null

export function createVesselPaths(orgId: string, vesselId: string): VesselPaths {
  // Use JSON.stringify to safely create cache key without collision risk
  const cacheKey = JSON.stringify([orgId, vesselId])
  const cached = vesselPathsCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const base = `/api/pay/org/${enc(orgId)}/vessel/${enc(vesselId)}`

  const paths: VesselPaths = {
    invite: () => `${base}/users/invite`,
    vesselUsersInvites: () => `${base}/users/invites`,
    connectedAccounts: () => `${base}/connected-accounts`,
    connectedAccountsAvailability: () => `${base}/connected-accounts/availability`,
    activateModule: () => `${base}/activate`,
    cashFloatsAvailable: () => `${base}/cash-float/available`,
    cashTransfer: () => `${base}/cash/transfer`,
  }

  vesselPathsCache.set(cacheKey, paths)
  return paths
}

/**
 * Pay API path builder for vessel-scoped connected account endpoints.
 * Returns a builder object with methods for all pay-related endpoints under org/vessel/connected-account.
 * Results are memoized per orgId/vesselId/connectedAccountId combination.
 *
 * Usage:
 *   createVesselConnectedAccountPaths(orgId, vesselId, caId).cards()
 *   createVesselConnectedAccountPaths(orgId, vesselId, caId).cardById(cardId)
 */
export function createVesselConnectedAccountPaths(
  orgId: string,
  vesselId: string,
  connectedAccountId: string,
): VesselConnectedAccountPaths {
  const cacheKey = JSON.stringify([orgId, vesselId, connectedAccountId])
  const cached = vesselConnectedAccountPathsCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const base = `/api/pay/org/${enc(orgId)}/vessel/${enc(vesselId)}/connected-account/${enc(connectedAccountId)}`

  const paths: VesselConnectedAccountPaths = {
    // Beneficiaries
    beneficiaries: () => `${base}/beneficiaries`,
    beneficiaryById: (beneficiaryId: string) => `${base}/beneficiary/${enc(beneficiaryId)}`,
    beneficiary: () => `${base}/beneficiary`,

    // Cards
    cards: () => `${base}/cards`,
    cardById: (cardId: string) => `${base}/card/${enc(cardId)}`,
    cardLimits: (cardId: string) => `${base}/card/${enc(cardId)}/limits`,
    cardSpendingStats: (cardId: string) => `${base}/card/${enc(cardId)}/spending-stats`,
    cardSearch: (cardId: string) => `${base}/card/${enc(cardId)}/search`,
    cardDeliveryDetails: (cardId: string) => `${base}/card/${enc(cardId)}/delivery-details`,
    cardStatus: (cardId: string) => `${base}/card/${enc(cardId)}/status`,
    cardActivate: (cardId: string) => `${base}/card/${enc(cardId)}/activate`,
    myCardStatus: (cardId: string) => `${base}/my-card/${enc(cardId)}/status`,
    cardPanToken: (cardId: string) => `${base}/card/${enc(cardId)}/pan-token`,
    myCards: () => `${base}/my-cards`,

    // Cardholders
    cardholders: () => `${base}/cardholders`,
    cardholdersAvailable: () => `${base}/cardholders/new/available`,
    cardholder: () => `${base}/cardholder`,
    cardholderById: (cardholderId: string) => `${base}/cardholder/${enc(cardholderId)}`,
    cardholderCard: (cardholderId: string) => `${base}/cardholder/${enc(cardholderId)}/card`,
    cardholderDetails: (cardholderId: string) => `${base}/cardholder/${enc(cardholderId)}/details`,

    // Transactions
    categories: () => `${base}/categories/user`,
    transactions: () => `${base}/transactions`,
    myTransactions: () => `${base}/my-transactions`,
    transactionById: (transactionId: string) => `${base}/transaction/${enc(transactionId)}`,
    transactionEditInspect: (transactionId: string) => `${base}/transaction/${enc(transactionId)}/inspect`,
    transactionCommit: (transactionId: string) => `${base}/transaction/${enc(transactionId)}/commit`,
    transactionStatus: (transactionId: string) => `${base}/transaction/${enc(transactionId)}/status`,
    transactionStatusInspect: (transactionId: string) => `${base}/transaction/${enc(transactionId)}/status/inspect`,
    transactionPaymentInitiation: (transactionId: string) =>
      `${base}/transaction/${enc(transactionId)}/payment-initiation`,
    transactionSettlementStatus: (transactionId: string) =>
      `${base}/transaction/${enc(transactionId)}/settlement-status`,
    transactionTransfer: (transactionId: string) => `${base}/transaction/${enc(transactionId)}/transfer`,
    transactionTransferFees: (transactionId: string) => `${base}/transaction/${enc(transactionId)}/transfer-fees`,
    transactionAttachments: (transactionId: string) => `${base}/transaction/${enc(transactionId)}/attachments`,
    transactionAttachmentById: (transactionId: string, attachmentId: string) =>
      `${base}/transaction/${enc(transactionId)}/attachments/${enc(attachmentId)}`,
    transactionAttachmentPrepare: (transactionId: string) =>
      `${base}/transaction/${enc(transactionId)}/attachment/prepare`,
    transactionAttachmentConfirm: (transactionId: string) =>
      `${base}/transaction/${enc(transactionId)}/attachment/confirm`,
    transactionActivityLogs: (transactionId: string) => `${base}/transaction/${enc(transactionId)}/activity-logs`,
    opsTransaction: () => `${base}/ops/transaction`,
    opsTransactionById: (transactionId: string) => `${base}/ops/transaction/${enc(transactionId)}`,
    receiptCapture: () => `${base}/receipt-capture`,
    taxLabels: () => `${base}/tax-labels`,

    // Charters
    chartersSearch: () => `${base}/charters/search`,
    chartersForTransactions: () => `${base}/transactions/charters`,
    charter: () => `${base}/charter`,
    charterById: (charterId: string) => `${base}/charter/${enc(charterId)}`,
    charterStats: (charterId: string) => `${base}/charter/${enc(charterId)}/stats`,
    charterTransactions: (charterId: string) => `${base}/charter/${enc(charterId)}/transactions`,
    charterLedger: (charterId: string) => `${base}/charter/${enc(charterId)}/ledger`,
    charterSigningOffLedger: (charterId: string) => `${base}/charter/${enc(charterId)}/signing-off-ledger`,
    charterDigitalSignOffLedger: (charterId: string) => `${base}/charter/${enc(charterId)}/digital-sign-off-ledger`,
    charterDigitalSigningOffLedger: (charterId: string) =>
      `${base}/charter/${enc(charterId)}/digital-signing-off-ledger`,
    charterTransaction: (charterId: string) => `${base}/charter/${enc(charterId)}/transaction`,
    charterAttachments: (charterId: string) => `${base}/charter/${enc(charterId)}/attachments`,
    charterAttachmentById: (charterId: string, attachmentId: string) =>
      `${base}/charter/${enc(charterId)}/attachments/${enc(attachmentId)}`,
    charterAttachmentPrepare: (charterId: string) => `${base}/charter/${enc(charterId)}/attachment/prepare`,
    charterAttachmentConfirm: (charterId: string) => `${base}/charter/${enc(charterId)}/attachment/confirm`,

    // Deposits
    deposits: () => `${base}/deposits`,
    depositById: (depositId: string) => `${base}/deposits/${enc(depositId)}`,
    depositAttribution: () => `${base}/deposit-attribution`,

    // Cash (operations)
    cashFloats: () => `${base}/cash-float`,
    cashFloatById: (cashFloatId: string) => `${base}/cash-float/${enc(cashFloatId)}`,
    cashFloatBalance: (cashFloatId: string) => `${base}/cash-float/${enc(cashFloatId)}/balance`,
    cashFloatActivity: (cashFloatId: string) => `${base}/cash-float/${enc(cashFloatId)}/activity`,
    cashFloatTopUp: (cashFloatId: string) => `${base}/cash-float/${enc(cashFloatId)}/top-up`,
    cashSummary: () => `${base}/cash/summary`,

    // Users
    connectedAccountUsersInvites: () => `${base}/users/invites`,
    connectedAccountAllUsers: () => `${base}/all-users`,
    addUsersToConnectedAccount: () => `${base}/users`,
    removeUsersFromConnectedAccount: () => `${base}/users`,
    onboardingAuthorize: () => `${base}/onboarding/authorize`,
    rfiAuthorize: () => `${base}/rfi/authorize`,

    // Wallet / Global Accounts
    balancesHistory: () => `${base}/balances/history`,
    balancesFinancialTransactionBySource: (source: string) =>
      `${base}/balances/financial-transactions/source/${enc(source)}`,
    balancesTotalEstimate: (currency: string) => `${base}/balances/total-estimate/${enc(currency)}`,
    balancesCurrent: () => `${base}/balances/current`,
    globalAccounts: () => `${base}/global-accounts`,
    globalAccount: () => `${base}/global-account`,
    globalAccountById: (globalAccountId: string) => `${base}/global-account/${enc(globalAccountId)}`,
    // @deprecated - used as a fallback if the feature flag "PAYOUT_TRANSFER" is disabled. The enabled flow should use transfers()
    payout: () => `${base}/payout`,
    transfers: () => `${base}/transfers`,
    transferById: (transferId: string) => `${base}/transfers/${enc(transferId)}`,
    transfersValidate: () => `${base}/transfers/validate`,
    payoutAuthorize: () => `${base}/payout/authorize`,
    fxQuotes: () => `${base}/fx/quotes`,
    fxQuoteById: (quoteId: string) => `${base}/fx/quotes/${enc(quoteId)}`,
    fxConversions: () => `${base}/fx/conversions`,
    fxConversionById: (conversionId: string) => `${base}/fx/conversions/${enc(conversionId)}`,
    balancesFinancialTransactions: () => `${base}/balances/financial-transactions`,
    balancesFinancialTransactionById: (transactionId: string) =>
      `${base}/balances/financial-transactions/${enc(transactionId)}`,
    scaAuthorize: () => `${base}/sca/authorize`,
    connectedAccountTransfer: () => `${base}/connected-account-transfer`,

    // Reports
    financialReports: () => `${base}/financial-reports`,
    financialReportById: (reportId: string) => `${base}/financial-reports/${enc(reportId)}`,
    generatedReports: () => `${base}/generated-reports`,
    generatedReportById: (reportId: string) => `${base}/generated-reports/${enc(reportId)}`,
    generatedReportsExpenseReport: () => `${base}/generated-reports/expense-report`,
    generatedReportsXeroBankStatement: () => `${base}/generated-reports/xero-bank-statement`,

    // Owes
    apaOwesOpsTransactions: () => `${base}/apa-owes-ops/transactions`,
    transactionConnectedAccountTransfer: (transactionId: string) =>
      `${base}/transaction/${enc(transactionId)}/connected-account-transfer`,

    // Xero
    xeroConnectionSync: () => `${base}/xero/connection-sync`,

    // QuickBooks
    quickbooksConnectionSync: () => `${base}/quickbooks/connection-sync`,

    // Approval workflows
    approvalWorkflows: () => `${base}/approval-workflows`,
    approvalWorkflowById: (workflowId: string) => `${base}/approval-workflows/${enc(workflowId)}`,
    approvalWorkflowSettings: () => `${base}/approval-workflow-settings`,
    approvalInitiatorCandidates: () => `${base}/approval-initiator-candidates`,
  }

  vesselConnectedAccountPathsCache.set(cacheKey, paths)
  return paths
}

/**
 * IAM organization-level API paths (not requiring connected account).
 * These are for IAM organization-scoped endpoints.
 * Results are memoized per orgId.
 */
export function createIamOrganizationPaths(orgId: string): IamOrganizationPaths {
  const cached = iamOrganizationPathsCache.get(orgId)
  if (cached) {
    return cached
  }

  const base = `/api/iam/org/${enc(orgId)}`

  const paths: IamOrganizationPaths = {
    // Organisation users
    organizationUsers: () => `${base}/users`,
    organizationUsersWithAccess: () => `${base}/users/access`,
    organizationUser: (userId: string) => `${base}/users/${enc(userId)}`,
    organizationUserRoles: (userId: string) => `${base}/users/${enc(userId)}/roles`,
    leaveOrganization: () => `${base}/leave`,

    // Vessel users
    vesselUsers: (vesselId: string) => `${base}/vessel/${enc(vesselId)}/users`,
  }

  iamOrganizationPathsCache.set(orgId, paths)
  return paths
}

/**
 * Organization-level API paths (not requiring connected account).
 * These are for organization-scoped endpoints.
 * Results are memoized per orgId.
 */
export function createOrganizationPaths(orgId: string): OrganizationPaths {
  const cached = organizationPathsCache.get(orgId)
  if (cached) {
    return cached
  }

  const base = `/api/pay/org/${enc(orgId)}`

  const paths: OrganizationPaths = {
    // Invites
    organizationInvites: () => `${base}/users/invites`,
    createOrganizationInvite: () => `${base}/users/invite`,
    getOrganizationInvite: (inviteId: string) => `${base}/users/invite/${enc(inviteId)}`,
    deleteOrganizationInvite: (inviteId: string) => `${base}/users/invite/${enc(inviteId)}`,

    // User Access
    userAccessToConnectedAccounts: () => `${base}/connected-accounts/user-access`,
    addUserToConnectedAccounts: () => `${base}/connected-accounts/user`,
    removeUserFromConnectedAccounts: () => `${base}/connected-accounts/user`,

    // Categories
    categoriesAll: () => `${base}/categories/all`,
    category: () => `${base}/category`,
    categoryById: (categoryId: string) => `${base}/category/${enc(categoryId)}`,

    // Vessels
    vessels: () => `${base}/vessel`,
    vesselConnectedAccounts: (vesselId: string) => `${base}/vessel/${enc(vesselId)}/connected-accounts`,
    vesselConnectedAccountsAvailability: (vesselId: string) =>
      `${base}/vessel/${enc(vesselId)}/connected-accounts/availability`,

    // Integrations
    integrations: () => `${base}/integrations`,
    integrationsToggle: () => `${base}/integrations/toggle`,

    // Xero
    xeroOrganizations: () => `${base}/xero/organizations`,
    xeroOrgMappings: () => `${base}/xero/org-mappings`,
    xeroEntityVessels: () => `${base}/xero/entity-vessels`,
    xeroChartOfAccounts: () => `${base}/xero/chart-of-accounts`,
    xeroTaxRateImports: () => `${base}/xero/tax-rate-imports`,
    xeroTaxRates: (xeroTenantId: string) => `${base}/xero/tax-rates?xeroTenantId=${enc(xeroTenantId)}`,
    xeroTaxRateMappings: () => `${base}/xero/tax-rate-mappings`,
    xeroBankAccounts: (xeroTenantId: string) => `${base}/xero/bank-accounts?xeroTenantId=${enc(xeroTenantId)}`,
    xeroCurrencyBankMappings: () => `${base}/xero/currency-bank-mappings`,
    xeroTrackingCategories: () => `${base}/xero/tracking-categories`,
  }

  organizationPathsCache.set(orgId, paths)
  return paths
}

/**
 * Organization + connected-account level API paths without vessel scope.
 * Used for endpoints shaped like /pay/org/:orgId/connected-account/:connectedAccountId/*
 */
export function createOrganizationConnectedAccountPaths(
  orgId: string,
  connectedAccountId: string,
): OrganizationConnectedAccountPaths {
  const cacheKey = JSON.stringify([orgId, connectedAccountId])
  const cached = organizationConnectedAccountPathsCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const base = `/api/pay/org/${enc(orgId)}/connected-account/${enc(connectedAccountId)}`
  const paths: OrganizationConnectedAccountPaths = {
    xeroTrackingOptions: () => `${base}/xero/tracking-options`,
    quickbooksConfiguration: () => `${base}/quickbooks`,
    quickbooksConnection: () => `${base}/quickbooks/connection`,
    quickbooksAccounts: () => `${base}/quickbooks/accounts`,
    quickbooksBankAccounts: () => `${base}/quickbooks/bank-accounts`,
    quickbooksCurrencyBankMappings: () => `${base}/quickbooks/currency-bank-mappings`,
    quickbooksTaxCodes: () => `${base}/quickbooks/tax-codes`,
    quickbooksTaxMappings: () => `${base}/quickbooks/tax-mappings`,
  }

  organizationConnectedAccountPathsCache.set(cacheKey, paths)
  return paths
}

/**
 * Integration API paths (Xero OAuth, etc.). Results are memoized per orgId.
 */
export function createIntegrationPaths(orgId: string): IntegrationPaths {
  const cached = integrationPathsCache.get(orgId)
  if (cached) {
    return cached
  }

  const paths: IntegrationPaths = {
    xeroOAuthConnect: () => `/api/integration/xero/${enc(orgId)}/oauth/connect`,
    quickbooksOAuthConnect: () => `/api/integration/quickbooks/${enc(orgId)}/oauth/connect`,
  }

  integrationPathsCache.set(orgId, paths)
  return paths
}

/**
 * Standalone API paths that are not associated with a specific organization or connected account.
 * These are for IAM, config, and other non-pay endpoints.
 * Results are memoized (singleton instance since there are no parameters).
 */
export function createRootPaths(): RootPaths {
  if (rootPathsCache) {
    return rootPathsCache
  }

  rootPathsCache = {
    // Users (IAM endpoints)
    me: () => '/api/iam/me',
    mePreferences: () => '/api/iam/me/preferences',

    // Invites (public endpoint, no auth required)
    getInvite: (inviteId: string) => `/api/pay/user/invite/${enc(inviteId)}`,

    // Config (public endpoint, no auth required)
    config: () => '/api/config',
  } as const

  return rootPathsCache
}
